import * as Sentry from '@sentry/browser';
import { HttpStatusCodes } from '@zocdoc-frontend-common/utils-constants';
import { fetchWithDatadog } from '@zocdoc-frontend-common/utils-react';

/**
 * @property {string} username email
 * @property {string} password
 * @returns {boolean|null} authenticatePassword
 */

export async function authenticatePassword({
    username,
    password,
    application,
}) {
    const API_HOST =
        process.env.STORYBOOK_SIGN_IN_API_HOST || process.env.API_HOST || '';

    const endpoint = `${API_HOST}/accounts/v2/authentication/authenticate-password`;

    const body = {
        username: username,
        password: password,
    };

    const response = await fetchWithDatadog({
        body: JSON.stringify(body),
        endpoint,
        method: 'POST',
        operationId: 'authenticate_password_mfa_post',
        pageName: 'sign-in',
        application,
    });

    if (response?.status === HttpStatusCodes.AuthenticationTimeout) {
        const body = await response.json();
        return {
            statusCode: HttpStatusCodes.AuthenticationTimeout,
            body: {
                redirectUrl: body.redirect_url,
            },
        };
    }

    if (response?.status === HttpStatusCodes.BadRequest) {
        const body = await response.json();
        if (body?.redirect_url) {
            return {
                statusCode: HttpStatusCodes.BadRequest,
                body: {
                    redirectUrl: body.redirect_url,
                },
            };
        } else {
            return {
                statusCode: HttpStatusCodes.BadRequest,
                body: {
                    error: body.error_message,
                },
            };
        }
    }

    // successful response for user w/o mfa enabled
    if (response?.status === HttpStatusCodes.OK) {
        const body = await response.json();
        return {
            statusCode: HttpStatusCodes.OK,
            body: {
                entityId: body.entity_id,
                entityType: body.entity_type,
                redirectUrl: body.redirect_url,
            },
        };
    }

    // successful response for mfa-enabled user
    // initiates sending of verification code
    if (response?.status === HttpStatusCodes.Created) {
        const body = await response.json();
        return {
            statusCode: HttpStatusCodes.Created,
            body: {
                verificationCodeDestination: body.verification_code_destination,
                verificationId: body.verification_id,
                verificationToken: body.verification_token,
                verificationChannel: body.verification_channel,
            },
        };
    }

    if (response?.status === HttpStatusCodes.Unauthorized) {
        return {
            statusCode: HttpStatusCodes.Unauthorized,
        };
    }

    if (response?.status >= 400) {
        const body = await response.json();
        if (body?.error_message) {
            return {
                statusCode: response.status,
                body: {
                    error: body?.error_message,
                },
            };
        } else {
            return {
                statusCode: response.status,
            };
        }
    }

    Sentry.withScope((scope) => {
        scope.setExtras({ statusText: response?.statusText });
        scope.setTag('status_code', response?.status);
        Sentry.captureException(
            Error(`Failed to authenticate user's email and password`)
        );
    });

    return null;
}
